/**
 * Default stock threshold for determining limited stock status
 * @constant {number}
 */
const DEFAULT_STOCK_THRESHOLD = 30;

/**
 * @typedef {Object} ProductInventoryData
 * @property {number} totalInventory - Total inventory quantity across all variants
 * @property {boolean} hasLimitedStock - Whether the product has limited stock
 * @property {boolean} inStock - Whether the product is in stock
 * @property {string | null} stockMessage - Message to display for limited stock
 */

/**
 * Fetches product inventory data from Shopify
 *
 * @param {string} productHandle - The product handle or ID
 * @param {number} [stockThreshold=DEFAULT_STOCK_THRESHOLD] - The threshold for determining limited stock
 * @returns {Promise<ProductInventoryData | null>} The product inventory data or null if error
 */
export async function getProductInventory(
  productHandle,
  stockThreshold = DEFAULT_STOCK_THRESHOLD
) {
  try {
    ShopifyInventoryClient.domain = "zahc7f-9g.myshopify.com";
    ShopifyInventoryClient.token = "8d494e595363d2da9a03813baf6bf85e"; // e.g., a theme setting or metafield value
    ShopifyInventoryClient.apiVersion = "2025-10"; // optional override

    const productHandle = "padelsmith-001";

    const totalInventorySet =
      await ShopifyInventoryClient.fetchProductInventory(productHandle)
        .then(({ totalInventory }) => {
          return totalInventory;
        })
        .catch((error) => console.error("Inventory lookup failed", error));

    const hasLimitedStock =
      totalInventorySet > 0 && totalInventorySet <= stockThreshold;
    const inStock = totalInventorySet > 0;

    let stockMessage = null;
    if (totalInventorySet > 20 && totalInventorySet <= 30) {
      stockMessage = "Limited rackets available!";
    } else if (totalInventorySet > 0 && totalInventorySet <= 20) {
      stockMessage = "Last few remaining!";
    }

    return {
      totalInventorySet,
      hasLimitedStock,
      inStock,
      stockMessage: hasLimitedStock ? stockMessage : null,
    };
  } catch (error) {
    console.error("Error fetching product inventory:", error);
    return null;
  }
}

/**
 * Checks if a product has limited stock
 *
 * @param {string} productHandle - The product handle or ID
 * @param {number} [stockThreshold=DEFAULT_STOCK_THRESHOLD] - The threshold for determining limited stock
 * @returns {Promise<boolean>} True if product has limited stock, false otherwise
 */
export async function hasLimitedStock(
  productHandle,
  stockThreshold = DEFAULT_STOCK_THRESHOLD
) {
  const inventory = await getProductInventory(productHandle, stockThreshold);
  return inventory?.hasLimitedStock ?? false;
}

/**
 * Gets the total inventory count for a product
 *
 * @param {string} productHandle - The product handle or ID
 * @returns {Promise<number>} The total inventory count, or 0 if error
 */
export async function getTotalInventory(productHandle) {
  const inventory = await getProductInventory(productHandle);
  return inventory?.totalInventory ?? 0;
}

/**
 * Creates and displays an inventory banner
 *
 * @param {string} message - The stock message to display
 */
function displayInventoryBanner(message) {
  // Check if banner already exists
  if (document.querySelector(".product-inventory-banner")) return;

  const banner = document.createElement("div");
  banner.className = "product-inventory-banner";
  banner.textContent = message;
  banner.setAttribute("role", "status");
  banner.setAttribute("aria-live", "polite");

  // Insert banner at the top of the body or main content area
  const mainContent = document.body;
  mainContent.insertBefore(banner, mainContent.firstChild);
}

/**
 * Initialize inventory display for the single product
 */
async function initProductInventory() {
  const productHandle = "padelsmith-001";

  try {
    const inventory = await getProductInventory(productHandle);
    console.log(inventory);

    if (inventory?.stockMessage) {
      // displayInventoryBanner(inventory.stockMessage);
    }
  } catch (error) {
    console.error(`Error fetching inventory for ${productHandle}:`, error);
  }
}

// Initialize on page load
if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", initProductInventory);
} else {
  initProductInventory();
}
